/**
 * "Errors" gadget (Verify): the requests that failed (Traces, `GET /traces?status=failed&since=24h`; Flow keeps less than
 * a day, so the window the Traces map says it covers is the one named) and the services that crashed or whose build
 * failed. Each request opens its trace (`?tab=traces&trace=<id>`), each service its log; the links only where the plan
 * shows those tabs, "Ask the agent" only where chat is on. Tracing off: says so.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ago } from '../../../utils/blameModel';
import { shortServiceName } from '../../../utils/gitRepoSource';
import { brokenServices, failedRequests } from '../../../utils/homeGadgets/verify';
import { openHistory } from '../../../utils/workspaceModel';
import { windowLabel } from '../RequestsGadget';
import GadgetCard, { Quiet, TabLink } from './GadgetCard';
import { useFailedTraces, useTracesMap } from './sources';

const isoOf = (ms) => (Number.isFinite(ms) ? new Date(ms).toISOString() : null);

/** Opens Traces on one request (`?tab=traces&trace=<id>`: the view's focus, which the URL and the tab follow). */
export function openTraceInTab(traceId) {
  if (typeof window === 'undefined' || !traceId) { return; }
  openHistory({ tab: 'traces', focus: { trace: traceId } });
}

const STATE = { crashed: 'Crashed', failed: 'Build failed' };

export default function ErrorsGadget({ projectId, items = [], onOpenTrace = null, onOpenTraces = null, onOpenLogs = null, onAskAgent = null }) {
  const failed = useFailedTraces(projectId);
  const map = useTracesMap(projectId);
  const requests = failedRequests(failed.data);
  const broken = brokenServices(items);
  const tracingOff = map.data?.enabled === false || failed.data?.enabled === false;
  const total = failed.data?.counts?.failed ?? failed.data?.total ?? requests.length;
  const span = map.data ? windowLabel(map.data) : 'last 24 hours';
  let body;
  if (!failed.data && failed.loading) { body = <Quiet testId="gadget-loading">Reading the failed requests…</Quiet>; }
  else if (requests.length === 0 && broken.length === 0) {
    body = (
      <Quiet testId="gadget-empty">
        {failed.error && !failed.data ? 'The failed requests could not be read just now; no service crashed.'
          : tracingOff ? 'Tracing is off, so failed requests are not known; no service crashed.'
            : `No failed request in the ${span}, and no service crashed.`}
      </Quiet>
    );
  } else {
    body = (
      <>
        <ul className="gd-rows">
          {broken.map((b) => (
            <li className="gd-row" data-testid={`errors-service-${shortServiceName(b.serviceName)}`} key={b.serviceName}>
              <strong>{shortServiceName(b.serviceName)}</strong>
              <span className="gd-grow gd-bad">{STATE[b.state]}</span>
              <TabLink onClick={onOpenLogs ? () => onOpenLogs(b.serviceName) : null}>Logs</TabLink>
              <TabLink onClick={onAskAgent ? () => onAskAgent(`${shortServiceName(b.serviceName)}: ${STATE[b.state].toLowerCase()}. Read its log and fix it.`) : null}>Ask the agent</TabLink>
            </li>
          ))}
          {requests.map((r) => (
            <li className="gd-row" data-testid={`errors-trace-${r.traceId}`} key={r.traceId}>
              {r.httpStatus && <span className="gd-bad gd-num">{r.httpStatus}</span>}
              <span className="gd-grow">{r.entry}{r.service ? <span className="ph-muted"> · {r.service}</span> : null}</span>
              {r.at && <span className="ph-muted ph-small">{ago(isoOf(r.at))}</span>}
              <TabLink onClick={onOpenTrace ? () => onOpenTrace(r.traceId) : null}>Trace</TabLink>
              <TabLink onClick={onAskAgent ? () => onAskAgent(`The request ${r.entry} failed${r.httpStatus ? ` with ${r.httpStatus}` : ''} (trace ${r.traceId}). Find out why from its trace and fix it.`) : null}>Ask the agent</TabLink>
            </li>
          ))}
        </ul>
        {failed.error && !failed.data && <p className="gd-note">The failed requests could not be read just now.</p>}
      </>
    );
  }
  const count = (requests.length ? total : 0) + broken.length;
  return (
    <GadgetCard links={<TabLink onClick={onOpenTraces}>Traces</TabLink>} meta={count > 0 ? `${count} · ${span}` : null} testId="errors-gadget" title="Errors">
      {body}
    </GadgetCard>
  );
}

ErrorsGadget.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ serviceName: PropTypes.string, service: PropTypes.object })),
  onAskAgent: PropTypes.func,
  onOpenLogs: PropTypes.func,
  onOpenTrace: PropTypes.func,
  onOpenTraces: PropTypes.func,
  projectId: PropTypes.string.isRequired,
};
